import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiAccountsIndex, type AccountSummary } from "../lib/domains/accounts";
import { apiFundingBankConfig, type BankConfig } from "../lib/domains/funding";

export default function DepositsShow() {
  const { account } = useParams();
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AccountSummary | null>(null);
  const [bankConfig, setBankConfig] = useState<BankConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const accounts = await apiAccountsIndex();
        const found = accounts.find((item) => String(item.id) === String(account));

        if (!found) {
          if (alive) {
            setSelected(null);
            setBankConfig(null);
            setError("Account not found.");
          }
          return;
        }

        const config = await apiFundingBankConfig(found.currency || "EUR");

        if (alive) {
          setSelected(found);
          setBankConfig(config.bankConfig ?? null);
        }
      } catch (err: any) {
        if (alive) {
          setError(err?.message || "Could not load deposit instructions.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [account]);

  const rows = useMemo(() => {
    if (!bankConfig) return [];

    return [
      ["Beneficiary / provider", bankConfig.provider_name],
      ["Bank", bankConfig.bank_name],
      ["Bank address", bankConfig.bank_address],
      ["Bank country", bankConfig.bank_country],
      ["IBAN", bankConfig.iban],
      ["BIC / SWIFT", bankConfig.bic],
      ["Intermediary bank", bankConfig.intermediary_bank_name],
      ["Intermediary BIC", bankConfig.intermediary_bank_bic],
      ["Intermediary address", bankConfig.intermediary_bank_address],
    ].filter(([, value]) => Boolean(value));
  }, [bankConfig]);

  if (loading) {
    return <div className="section-box skeleton">Loading deposit instructions...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Deposit Instructions</h1>
          <p className="text-sm text-gray-500">
            Use these details only for deposits into this account.
          </p>
        </div>
        <Link to="/funding/create" className="btn">
          Create funding request
        </Link>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      {selected && (
        <div className="card-box space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <Summary label="Account" value={selected.label || `#${selected.id}`} />
            <Summary label="Currency" value={selected.currency || "EUR"} />
            <Summary label="Assigned IBAN" value={selected.iban || "Not assigned"} />
          </div>

          {rows.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full text-left text-sm">
                <tbody>
                  {rows.map(([label, value]) => (
                    <tr key={label} className="border-b border-gray-100 last:border-0">
                      <th className="w-56 bg-gray-50 px-4 py-3 font-semibold text-gray-600">
                        {label}
                      </th>
                      <td className="px-4 py-3 font-medium text-gray-900">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert-info">
              Funding instructions are not configured for {selected.currency || "this currency"} yet.
              Please contact support or use another approved account.
            </div>
          )}

          <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Always create a funding request before sending money so the reference and compliance
            document flow can be tracked.
          </div>
        </div>
      )}
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs font-semibold uppercase text-gray-500">{label}</div>
      <div className="mt-1 break-words text-base font-semibold text-gray-900">{value}</div>
    </div>
  );
}
